import { useState, useEffect } from 'react'
import { Server, Cpu, DollarSign, Check, X, Upload, Wifi, WifiOff } from 'lucide-react'

interface GpuConfig {
  worker_ip: string | null
  worker_port: number
  gpu_type: string
  gpu_count: number
  hourly_rate: number
  connected: boolean
}

interface WorkerStatus {
  running: boolean
  error?: string
  data?: {
    gpu_available: boolean
    gpu_name: string
    gpu_memory: string
    models_loaded: string[]
  }
}

interface DeployStatus {
  deploying: boolean
  progress: string
  error: string | null
}

interface SettingsProps {
  compact?: boolean
}

export function Settings({ compact = false }: SettingsProps) {
  const [config, setConfig] = useState<GpuConfig | null>(null)
  const [workerIp, setWorkerIp] = useState('')
  const [workerPort, setWorkerPort] = useState('8000')
  const [gpuType, setGpuType] = useState('A100-80GB')
  const [gpuCount, setGpuCount] = useState('8')
  const [hourlyRate, setHourlyRate] = useState('14.32')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus | null>(null)
  const [checkingStatus, setCheckingStatus] = useState(false)
  const [deployStatus, setDeployStatus] = useState<DeployStatus | null>(null)
  const [deploying, setDeploying] = useState(false)

  useEffect(() => {
    fetchConfig()
  }, [])

  useEffect(() => {
    if (config?.worker_ip) {
      checkWorkerStatus()
    }
  }, [config?.worker_ip])

  useEffect(() => {
    let interval: number | undefined
    if (deploying) {
      interval = window.setInterval(() => {
        fetchDeployStatus()
      }, 2000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [deploying])

  const fetchConfig = async () => {
    try {
      const res = await fetch('/api/config')
      const data = await res.json()
      setConfig(data)
      setWorkerIp(data.worker_ip || '')
      setWorkerPort(data.worker_port?.toString() || '8000')
      setGpuType(data.gpu_type || 'A100-80GB')
      setGpuCount(data.gpu_count?.toString() || '8')
      setHourlyRate(data.hourly_rate?.toString() || '14.32')
    } catch {
      console.error('Failed to fetch config')
    }
  }

  const checkWorkerStatus = async () => {
    setCheckingStatus(true)
    try {
      const res = await fetch('/api/worker/status')
      const data = await res.json()
      setWorkerStatus(data)
    } catch {
      setWorkerStatus({ running: false, error: 'Failed to check status' })
    } finally {
      setCheckingStatus(false)
    }
  }

  const fetchDeployStatus = async () => {
    try {
      const res = await fetch('/api/deploy/status')
      const data = await res.json()
      setDeployStatus(data)
      if (!data.deploying && deploying) {
        setDeploying(false)
        if (!data.error) {
          setMessage({ type: 'success', text: 'Worker deployed successfully!' })
          checkWorkerStatus()
        } else {
          setMessage({ type: 'error', text: data.error })
        }
      }
    } catch {
      console.error('Failed to fetch deploy status')
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)

    try {
      const params = new URLSearchParams()
      if (workerIp) params.append('worker_ip', workerIp)
      params.append('worker_port', workerPort)
      params.append('gpu_type', gpuType)
      params.append('gpu_count', gpuCount)
      params.append('hourly_rate', hourlyRate)

      const res = await fetch(`/api/config?${params.toString()}`, {
        method: 'POST'
      })

      if (res.ok) {
        setMessage({ type: 'success', text: 'Configuration saved!' })
        fetchConfig()
      } else {
        setMessage({ type: 'error', text: 'Failed to save configuration' })
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' })
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    setSaving(true)
    try {
      const params = new URLSearchParams()
      params.append('worker_port', workerPort)
      params.append('gpu_type', gpuType)
      params.append('gpu_count', gpuCount)
      params.append('hourly_rate', hourlyRate)

      await fetch(`/api/config?${params.toString()}`, { method: 'POST' })
      setWorkerIp('')
      setWorkerStatus(null)
      setMessage({ type: 'success', text: 'Disconnected from GPU worker' })
      fetchConfig()
    } catch {
      setMessage({ type: 'error', text: 'Failed to disconnect' })
    } finally {
      setSaving(false)
    }
  }

  const handleDeploy = async () => {
    if (!workerIp) {
      setMessage({ type: 'error', text: 'Enter worker IP first' })
      return
    }

    await handleSave()
    
    setDeploying(true)
    setMessage(null)
    setDeployStatus({ deploying: true, progress: 'Starting deployment...', error: null })

    try {
      const res = await fetch('/api/deploy/worker', { method: 'POST' })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.detail || 'Deployment failed')
      }
    } catch (e) {
      setDeploying(false)
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Deployment failed' })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className={`${
          config?.connected
            ? 'status-success'
            : 'status-warning'
        }`}>
          {config?.connected ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Connected to {config.worker_ip}
            </>
          ) : (
            <>
              <X className="w-3.5 h-3.5" />
              No worker connected
            </>
          )}
        </div>

        {config?.connected && (
          <div className={`${
            workerStatus?.running
              ? 'status-info'
              : 'status-error'
          }`}>
            {checkingStatus ? (
              <>
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Checking...
              </>
            ) : workerStatus?.running ? (
              <>
                <Wifi className="w-3.5 h-3.5" />
                {workerStatus.data?.gpu_name || 'Worker running'}
              </>
            ) : (
              <>
                <WifiOff className="w-3.5 h-3.5" />
                Not responding
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="label flex items-center gap-1">
            <Server className="w-3.5 h-3.5" />
            Worker IP
          </label>
          <input
            type="text"
            value={workerIp}
            onChange={(e) => setWorkerIp(e.target.value)}
            placeholder="192.168.1.100"
            className="input w-full"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Port</label>
            <input
              type="number"
              value={workerPort}
              onChange={(e) => setWorkerPort(e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label className="label flex items-center gap-1">
              <Cpu className="w-3.5 h-3.5" />
              GPU Type
            </label>
            <select
              value={gpuType}
              onChange={(e) => setGpuType(e.target.value)}
              className="input w-full"
            >
              <option value="A100-80GB">A100 80GB</option>
              <option value="A100-40GB">A100 40GB</option>
              <option value="A10">A10</option>
              <option value="RTX-4090">RTX 4090</option>
              <option value="H100">H100</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">GPU Count</label>
            <select
              value={gpuCount}
              onChange={(e) => setGpuCount(e.target.value)}
              className="input w-full"
            >
              {[1, 2, 4, 8].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" />
              $/hour
            </label>
            <input
              type="number"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              step="0.01"
              className="input w-full"
            />
          </div>
        </div>
      </div>

      {deploying && deployStatus && (
        <div className="p-3 rounded-xl bg-primary-500/10 border border-primary-500/20">
          <div className="flex items-center gap-2 text-primary-400 text-sm">
            <div className="w-4 h-4 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin" />
            {deployStatus.progress}
          </div>
        </div>
      )}

      {message && (
        <div className={`p-3 rounded-xl text-sm ${
          message.type === 'success'
            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving || deploying}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {workerIp ? 'Connect' : 'Save'}
          </button>

          {config?.connected && (
            <button
              onClick={handleDisconnect}
              disabled={saving || deploying}
              className="btn-secondary flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Disconnect
            </button>
          )}
        </div>

        {workerIp && (!workerStatus?.running || !config?.connected) && (
          <button
            onClick={handleDeploy}
            disabled={deploying || saving}
            className="btn-secondary flex items-center justify-center gap-2 w-full bg-violet-500/10 border-violet-500/20 hover:bg-violet-500/20 text-violet-300"
          >
            {deploying ? (
              <div className="w-4 h-4 border-2 border-violet-300/30 border-t-violet-300 rounded-full animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Deploy Worker
          </button>
        )}

        {config?.connected && !workerStatus?.running && !checkingStatus && (
          <button
            onClick={checkWorkerStatus}
            className="btn-secondary flex items-center justify-center gap-2 w-full"
          >
            <Wifi className="w-4 h-4" />
            Check Status
          </button>
        )}
      </div>

      {!compact && workerStatus?.data && (
        <div className="p-3 rounded-xl bg-surface-800/50 border border-white/5 text-xs text-gray-400">
          <div className="font-medium text-gray-300 mb-1">Worker Info</div>
          <div>GPU: {workerStatus.data.gpu_name}</div>
          <div>Memory: {workerStatus.data.gpu_memory}</div>
          <div>Models: {workerStatus.data.models_loaded?.join(', ') || 'None'}</div>
        </div>
      )}
    </div>
  )
}
